<template>
  <article class="wx-article" :lang="isEnglish ? 'en' : 'zh-CN'">
    <div class="wx-inner">
      <h1 class="wx-title">{{ article.title }}</h1>
      <template v-for="(block, index) in article.blocks" :key="index">
        <div v-if="block.type === 'image'" class="wx-media">
          <img :src="block.src" :alt="article.title" loading="lazy" />
        </div>
        <h2 v-else-if="block.type === 'h2'" class="wx-h2">{{ block.text }}</h2>
        <p v-else-if="block.type === 'p'" class="wx-p">
          <span v-for="(seg, si) in block.segs" :key="si" :class="seg.c">
            <a v-if="seg.u" :href="seg.u" target="_blank" rel="noopener noreferrer">{{ seg.t }}</a>
            <template v-else>{{ seg.t }}</template>
          </span>
        </p>
        <p v-else-if="block.type === 'meta'" class="wx-meta">
          <span v-for="(seg, si) in block.segs" :key="si">
            <a v-if="seg.u" :href="seg.u" target="_blank" rel="noopener noreferrer">{{ seg.t }}</a>
            <template v-else>{{ seg.t }}</template>
          </span>
        </p>
      </template>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();
const isEnglish = computed(() => locale.value === "en");

// 图片放在媒体库，按顺序编号（P2.jpg 封面 / P3-P12 十大优势配图）
const IMG = "https://www.healthcode.com/media/20261029";

const img = (name) => ({ type: "image", src: `${IMG}/${name}` });
const h2 = (text) => ({ type: "h2", text });
const p = (...segs) => ({
  type: "p",
  segs: segs.map((s) => (typeof s === "string" ? { t: s, c: "" } : s)),
});
const b = (t) => ({ t, c: "b" });
const meta = (...segs) => ({
  type: "meta",
  segs: segs.map((s) => (typeof s === "string" ? { t: s, c: "" } : s)),
});
const link = (t, u) => ({ t, u });

const siteLinks = (site, phone, mail, addr) => [
  meta("网站：", link("www.healthcode.com", "https://www.healthcode.com"), " / ", link("www.ourproteinfactory.com", "https://www.ourproteinfactory.com"), " / ", link("www.d2plab.com", "https://www.d2plab.com")),
  meta(phone, " / ", "+86-400-770-5500 / +1-800-955-3390"),
  meta(mail, "customer@ourproteinfactory.com"),
  meta(addr, "上海市浦东新区"),
];

const content = {
  zh: {
    title: "D2Pi-AutoLab无细胞体外合成自动化实验室创新产品系列——十大优势，精彩助力！",
    blocks: [
      img("P2.jpg"),
      h2("全球首家 DNA 到蛋白质全自动化平台，助力 AI 蛋白设计与全球生命科学提速"),
      p(
        "蛋白质是生命功能的核心载体，也是 AI 蛋白设计、创新药物研发、合成生物学研究的核心瓶颈。传统细胞表达体系周期漫长、操作繁琐、通量受限、人为误差大，难以跟上 AI 算法快速输出序列的迭代节奏。"
      ),
      p(
        b("D2Pi-AutoLab 系统"),
        "，通量灵活，适用每天",
        b("1-10000"),
        " 种样品的实验室通量需求，",
        b("从单一样品的核酸提取、细胞培养、基因组鉴定，到高通量DNA/RNA制备、超螺旋质粒纯化、核酸回收、DNA/RNA浓缩、内毒素去除、体外蛋白质合成、纯化、活性和亲和力鉴定"),
        "等，均能实现自动化、高标准，用途广泛，加速推动全球生命科学研究进展。"
      ),
      h2("十大核心优势，重新定义蛋白合成自动化"),
      p("1.【", b("全球首家】"), "整合 DNA-to-Protein(D2P) 技术，实现从信息到分子的无缝制造。"),
      img("P3.png"),
      p("2.【", b("自主产权】"), "基于 C++ 原生开发，原创数据内核，具有完全自主知识产权的创新国产生物底层设计软件系统。"),
      img("P4.png"),
      p("3.【", b("D2Pi-2.0】"), "整合新一代 D2Pi-2.0 平台，实现真实世界万级蛋白质新设计分子的合成检测。"),
      img("P5.png"),
      p("4.【", b("毫秒级】"), "基因序列读取分析毫秒级完成，自动分类标。"),
      img("P6.png"),
      p("5.【", b("智能算法】"), "强大、灵活的序列比对算法，高效、便捷的序列拼接算法。"),
      img("P7.png"),
      p("6.【", b("基因数据】"), "AI 人工算法内核，2.4 亿基因蛋白质数据库，实现全新蛋白质的创新设计。"),
      img("P8.png"),
      p("7.【", b("AI 筛选】"), "AI 蛋白质分子智能组合和 AI 筛选。"),
      img("P9.png"),
      p("8.【", b("一键优化】"), "基因序列一键优化，蛋白质结构一键优化，便捷迅速，提高生产力。"),
      img("P10.png"),
      p("9.【", b("图形界面】"), "基因序列图形化编辑，多维度展示，兼顾宏观总览和微观细节需要。"),
      img("P11.png"),
      p("10.【", b("基因编辑】"), "强大的基因编辑算法，自动引物设计分析，一步解决基因改造设计烦恼。"),
      img("P12.png"),
      h2("结语"),
      p(
        "作为无细胞蛋白质合成产业化全球龙头企业，康码依托累计全球",
        b("700余专利件"),
        "，",
        b("1700余件知识产权"),
        "，累计开发了一系列无细胞蛋白质合成自动化智能方案！",
        b("D2Pi-AutoLab"),
        "，轻松实现",
        b("1-300以上的日合成通量"),
        "，",
        b("每种蛋白在毫升级以上体外合成体积，毫克级合成得率"),
        "（依序列不同）",
        b("，满足下游功能鉴定，活性分析，亲和力测试，结构解析，代谢鉴定等量化需求。")
      ),
      p("康码 D2Pi-AutoLab 全自动高通量体外合成设备，为全球AI蛋白质智造，提升物理基础“加速器”！"),
      p(
        "更多信息，请点击 ",
        link("www.OurProteinFactory.com", "https://www.ourproteinfactory.com"),
        " / ",
        link("www.d2plab.com", "https://www.d2plab.com"),
        " / ",
        link("www.healthcode.com", "https://www.healthcode.com"),
        "，或拨打 +86-400-770-5500 垂询方案。"
      ),
      ...siteLinks("网站：", "电话：", "邮箱：", "地址："),
    ],
  },
  en: {
    title:
      "D2Pi-AutoLab: Innovative Product Series for Automated Cell-Free In Vitro Synthesis Laboratories — Ten Advantages, Powering Your Research!",
    blocks: [
      img("P2.jpg"),
      h2("The World's First Fully Automated DNA-to-Protein Platform, Accelerating AI Protein Design and Global Life Science"),
      p(
        "Proteins are the core carriers of biological function and the core bottleneck in AI protein design, innovative drug discovery and synthetic biology research. Traditional cell-based expression systems are slow, labor-intensive, limited in throughput and prone to human error — they can hardly keep pace with the rapid iteration of sequences produced by AI algorithms."
      ),
      p(
        b("The D2Pi-AutoLab system"),
        " offers flexible throughput for laboratories requiring ",
        b("1–10,000"),
        " samples per day. ",
        b("From nucleic acid extraction, cell culture and genome identification for a single sample, to high-throughput DNA/RNA preparation, supercoiled plasmid purification, nucleic acid recovery, DNA/RNA concentration, endotoxin removal, in vitro protein synthesis, purification, activity and affinity characterization"),
        " — all can be performed automatically to high standards, with broad applications that accelerate global life science research."
      ),
      h2("Ten Core Advantages That Redefine Automated Protein Synthesis"),
      p("1.【", b("World First】"), " Integrates DNA-to-Protein (D2P) technology to achieve seamless manufacturing from information to molecule."),
      img("P3.png"),
      p("2.【", b("Proprietary】"), " Built on native C++ development with an original data kernel — an innovative domestic biological design software system with fully independent intellectual property rights."),
      img("P4.png"),
      p("3.【", b("D2Pi-2.0】"), " Integrates the new-generation D2Pi-2.0 platform, enabling synthesis and screening of tens of thousands of newly designed protein molecules in the real world."),
      img("P5.png"),
      p("4.【", b("Millisecond-level】"), " Gene sequence reading and analysis are completed in milliseconds, with automatic classification and annotation."),
      img("P6.png"),
      p("5.【", b("Smart Algorithms】"), " Powerful and flexible sequence alignment algorithms, plus efficient and convenient sequence assembly algorithms."),
      img("P7.png"),
      p("6.【", b("Genetic Data】"), " An AI algorithm kernel and a 240-million gene-protein database enable the innovative design of entirely new proteins."),
      img("P8.png"),
      p("7.【", b("AI Screening】"), " Intelligent combination of AI protein molecules and AI-based screening."),
      img("P9.png"),
      p("8.【", b("One-click Optimization】"), " One-click optimization of gene sequences and protein structures — fast, convenient and productivity-boosting."),
      img("P10.png"),
      p("9.【", b("Graphical Interface】"), " Graphical editing of gene sequences with multi-dimensional display, covering both macro overview and micro detail."),
      img("P11.png"),
      p("10.【", b("Gene Editing】"), " Powerful gene editing algorithms with automatic primer design and analysis — gene modification design solved in one step."),
      img("P12.png"),
      h2("Conclusion"),
      p(
        "As the global leader in the industrialization of cell-free protein synthesis, Healthcode — with ",
        b("more than 700 patents"),
        " and ",
        b("over 1,700 intellectual property items"),
        " worldwide — has developed a full series of automated, intelligent cell-free protein synthesis solutions. ",
        b("D2Pi-AutoLab"),
        " easily achieves a daily synthesis throughput of ",
        b("1–300 or more"),
        ", with in vitro synthesis volumes at milliliter level or above and ",
        b("milligram-level yields"),
        " (depending on the sequence), meeting quantitative downstream needs such as functional characterization, activity analysis, affinity testing, structural analysis and metabolic identification."
      ),
      p("Healthcode's D2Pi-AutoLab fully automated high-throughput in vitro synthesis equipment is a physical “accelerator” for global AI protein manufacturing!"),
      p(
        "For more information, please visit ",
        link("www.OurProteinFactory.com", "https://www.ourproteinfactory.com"),
        " / ",
        link("www.d2plab.com", "https://www.d2plab.com"),
        " / ",
        link("www.healthcode.com", "https://www.healthcode.com"),
        ", or call +86-400-770-5500 for a consultation."
      ),
      ...siteLinks("Website: ", "Tel: ", "Email: ", "Address: "),
    ],
  },
};

const article = computed(() => (isEnglish.value ? content.en : content.zh));
</script>

<style scoped>
.wx-article {
  width: 100%;
  min-height: 100vh;
  padding: 8rem 0 4rem;
  background: #fff;
}

.wx-inner {
  max-width: 677px;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  font-size: 17px;
  line-height: 1.75;
  letter-spacing: 0.034em;
  color: #333;
  text-align: justify;
  overflow-wrap: break-word;
}

.wx-title {
  margin: 0 0 24px;
  color: #222;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.4;
  text-align: left;
}

.wx-h2 {
  margin: 2.5em 8px 1.2em;
  padding: 0 0 6px;
  border-bottom: 2px solid rgb(15, 76, 129);
  color: rgb(17, 17, 17);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.03em;
  line-height: 1.4;
  text-align: left;
}

.wx-p {
  margin: 0 0 16px;
}

.wx-media {
  margin: 0 0 16px;
  text-align: center;
}

.wx-media img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
}

.wx-meta {
  margin: 0 0 6px;
  color: #666;
  font-size: 15px;
  line-height: 1.7;
  text-align: left;
}

.b {
  font-weight: 700;
}

.hl {
  color: rgb(0, 128, 255);
  font-weight: 700;
}

.wx-inner a {
  color: inherit;
  text-decoration: inherit;
}

.wx-inner a:hover {
  color: rgb(0, 128, 255);
}

@media (max-width: 640px) {
  .wx-article {
    padding: 6rem 0 3rem;
  }

  .wx-inner {
    font-size: 16px;
    line-height: 1.7;
  }

  .wx-title {
    font-size: 19px;
  }

  .wx-h2 {
    font-size: 18px;
    margin: 1.8em 0 0.9em;
  }
}
</style>
